import { useState } from "react";
import { AlarmClock, Copy, FileText, ListChecks, Loader2, Mail, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/time";
import { useApp } from "@/context/AppContext";
import { aiOn, useSettings } from "@/context/SettingsContext";

const ACTIONS = [
  ["enrich", "Enrich", Wand2], ["summary", "Summarise", FileText], ["breakdown", "Break it down", ListChecks],
  ["draft", "Draft email", Mail], ["snooze", "Smart snooze", AlarmClock],
];
const PATH = { enrich: "enrich", summary: "summary", breakdown: "breakdown", draft: "draft", snooze: "snooze-options" };

const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied")).catch(() => toast.error("Couldn't copy"));

const Result = ({ kind, d, r, onApply, onSnooze }) => {
  if (kind === "enrich") return (
    <div className="space-y-2" data-testid="ai-enrich-result">
      <p className="whitespace-pre-wrap">{d.details}</p>
      {d.next_step && <p><b>Next step:</b> {d.next_step}</p>}
      {d.missing?.length > 0 && <p className="text-amber-200"><b>Missing:</b> {d.missing.join(", ")}</p>}
      <button onClick={() => onApply(d.details)} className="rounded-full bg-cyan-400 px-3 py-1 text-xs font-semibold text-black hover:bg-cyan-300" data-testid="ai-enrich-apply">Use these details</button>
    </div>
  );
  if (kind === "summary") return <p className="whitespace-pre-wrap" data-testid="ai-summary-result">{d.summary}</p>;
  if (kind === "breakdown") return <p data-testid="ai-breakdown-result">Added {d.checklist.length - (r.checklist?.length || 0)} steps to the checklist below.</p>;
  if (kind === "draft") return (
    <div className="space-y-2" data-testid="ai-draft-result">
      <p className="font-semibold">{d.subject}</p>
      <p className="whitespace-pre-wrap">{d.body}</p>
      <button onClick={() => copy(`${d.subject}\n\n${d.body}`)} className="flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold hover:bg-white/5" data-testid="ai-draft-copy"><Copy className="h-3 w-3" /> Copy</button>
    </div>
  );
  return (
    <div className="space-y-1.5" data-testid="ai-snooze-result">
      {!d.options?.length && <p>No good options found. Try again.</p>}
      {d.options?.map((o, i) => (
        <button key={o.at} onClick={() => onSnooze(o.at)} className="flex w-full items-start justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors hover:bg-white/5" data-testid={`ai-snooze-option-${i}`}>
          <span><b>{o.label}</b><span className="block text-xs text-muted-foreground">{o.why}</span></span>
          <span className="shrink-0 font-mono text-xs text-muted-foreground">{fmtDate(o.at)}</span>
        </button>
      ))}
    </div>
  );
};

export const TicketAi = ({ r }) => {
  const { run, refresh } = useApp();
  const { team } = useSettings();
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState(null);
  const actions = ACTIONS.filter(([k]) => aiOn(team, k) && (k !== "snooze" || r.status !== "done"));
  if (!actions.length) return null;
  const go = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/ai/reminders/${r.id}/${PATH[kind]}`, kind === "draft" ? { kind: "email" } : undefined, { timeout: 60000 });
      setRes({ kind, d: data });
      if (kind === "breakdown") refresh();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const apply = (details) => run(() => api.patch(`/reminders/${r.id}`, { details }), "Details updated").then(() => setRes(null));
  const snooze = (until) => run(() => api.post(`/reminders/${r.id}/snooze`, { until }), `Snoozed until ${fmtDate(until)}`).then(() => setRes(null));
  return (
    <section className="rounded-xl border border-cyan-400/20 bg-cyan-400/[0.04] p-3" data-testid="ticket-ai">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-cyan-300"><Sparkles className="h-3.5 w-3.5" /> AI helpers</p>
      <div className="flex flex-wrap gap-1.5">
        {actions.map(([k, label, Icon]) => (
          <button key={k} onClick={() => go(k)} disabled={!!busy} data-testid={`ticket-ai-${k}`} className="flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors hover:bg-white/5 disabled:opacity-50">
            {busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {label}
          </button>
        ))}
      </div>
      {res && <div className="mt-3 rounded-lg bg-background/50 p-3 text-sm"><Result {...res} r={r} onApply={apply} onSnooze={snooze} /></div>}
    </section>
  );
};
